import type {FC} from 'react'
import {useState, createContext, useMemo, useContext} from 'react'

import {breakpoints} from 'styles/theme'
import {useMobileNavigationVisibility} from '~/navigation/provider'
import {useMediaQuery} from '~/web-platform/use-media-query'

import {useUser} from './user'

interface WidgetsAPI {
    isWidgetsVisible: boolean
    showWidgets: (show: boolean) => void
    isHelpWidgetOpen: boolean
    setIsHelpWidgetOpen: React.Dispatch<React.SetStateAction<boolean>>
}

const WidgetsContext = createContext<WidgetsAPI>({
    isWidgetsVisible: true,
    showWidgets: () => {},
    isHelpWidgetOpen: false,
    setIsHelpWidgetOpen: () => {},
})

export const WidgetsProvider: FC = ({children}) => {
    const user = useUser()
    const acceptedTerms = user?.status?.acceptedTerms ?? false
    const isTablet = useMediaQuery(`(max-width: ${breakpoints.tabletMedUp})`)
    const [showWidgets, setShowWidgets] = useState(true)
    const {isMobileNavigationVisible} = useMobileNavigationVisibility()
    const [isHelpWidgetOpen, setIsHelpWidgetOpen] = useState(false)

    const contextValue = useMemo<WidgetsAPI>(() => {
        return {
            isWidgetsVisible: isTablet ? isMobileNavigationVisible || !acceptedTerms : showWidgets,
            isHelpWidgetOpen,
            setIsHelpWidgetOpen,
            showWidgets: setShowWidgets,
        }
    }, [
        acceptedTerms,
        isTablet,
        showWidgets,
        isMobileNavigationVisible,
        isHelpWidgetOpen,
        setIsHelpWidgetOpen,
    ])

    return <WidgetsContext.Provider value={contextValue}>{children}</WidgetsContext.Provider>
}

export const useWidgets = (): WidgetsAPI => {
    return useContext(WidgetsContext)
}
